/*
 * Authors: mariushirjanu
 * Created: 30/03/2022
 */

import * as _ from 'underscore';
import { EventEmitter, Injectable } from '@angular/core';
import { IObject } from '@type_definitions/datatypes';
import { IDashboardGroupMetadata, IDashboardMetadata, IDashboardReport, IDashboardSectionColumn, IDashboardSectionMetadata, IDashboardSectionValidation } from './dashboard-metadata.service';
import { util } from '@app/shared/util';
import { IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { FormlyType } from '@app/shared/formly/ga-formly.service';
import { IDashboardSection, IDashboardSectionStateRef } from './dashboard.component';
import { ReportFiltersUtil } from '@app/shared/report/report-filters-util.service';
import { IReportLayoutSetting, IReportMetadata } from '@app/shared/report/report-data.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { ScrollService } from '@app/shared/scroll/scroll.service';
import { ReportGridService } from '@app/shared/report/report-grid.service';
import { STATE_BROWSELIST, STATE_REPORT } from '@app/shared/app-states';

import { DashboardDataService, IDashboardMetadataReports, IDashboardStoredReports } from '@app/shared/report/dashboard-data.service';
import { IStoredReport } from '@app/shared/report/stored-report-data.service';
import { EVENT_DASHBOARD_REMOVE_REPORTS } from '@app/shared/app-events';
import { GaEventService } from '@app/shared/services/ga-event.service';
import { ReportLayoutSettingsService } from '@app/shared/report/report-layout-settings.service';
import { ReportParameterCode, ReportParameterShortCode } from '@app/main/report/report-state.util';

// depending on what fields are being visible in the KPIs, create the correct validation message for not filling in filters
export const VALIDATOR_FN_CUSTOMER:Function = (model:IObject, fieldLabels:string[]):string => (!model.customer && !model.customerCompany && !model.lam && !model.lamCompany && !model.lamGroup && !model.country && !model.gbdm) ? `Please select at least one of the following filters: ${util.join(_.without(fieldLabels, 'LAM Contact', 'GBDM Desk', 'Sub company'), ', ', ' or ')}.` : undefined;
export const VALIDATOR_FN_GBDM:Function = (model:IObject, fieldLabels:string[]) => (!model.lam && !model.lamCompany && !model.lamGroup && !model.gbdm && !model.gbdmDesk) ? `Please select at least one of the following filters: ${util.join(_.without(fieldLabels, 'LAM Contact', 'Country', 'Customer company', 'Customer', 'Sub company'), ', ', ' or ')}.` : undefined;

@Injectable()
export class DashboardService {
  public onFiltersInit:EventEmitter<void> = new EventEmitter<void>();
  private _appliedFilters:IObject;
  private _dashboardMetadata:IDashboardMetadata;
  private _dashboardReports:IDashboardSectionMetadata[];
  private _dashboardGroupReports:IDashboardGroupMetadata[];
  private _loadedMetadataReportsIds:number[] = []; // array containing ids of the loaded reports
  private _loadedMetadataReports:IDashboardMetadataReports = {}; // object containing metadata for all loaded reports
  private _loadedStoredReportCodes:string[] = []; // array containing codes of the loaded stored reports
  private _loadedStoredReports:IDashboardStoredReports = {}; // object containing all stored reports

  private scrollOffset:number = 15;

  constructor(
    private dashboardDataService:DashboardDataService,
    private reportLayoutSettingsService:ReportLayoutSettingsService,
    private eventService:GaEventService,
    private reportFiltersUtil:ReportFiltersUtil,
    private translationService:TranslationService,
    private reportGridService:ReportGridService,
    private scrollService:ScrollService
  ) {
    /***/
  }

  public emitEventFiltersInit():void {
    this.onFiltersInit.emit();
  }

  public set appliedFilters(filters:IObject){
    this._appliedFilters = filters;
  }

  public get appliedFilters():IObject {
    return this._appliedFilters;
  }

  public set dashboardMetadata(metadata:IDashboardMetadata){
    this._dashboardMetadata = metadata;
  }

  public get dashboardMetadata():IDashboardMetadata {
    return this._dashboardMetadata;
  }

  public set dashboardReports(dashboardReports:IDashboardSectionMetadata[]){
    this._dashboardReports = dashboardReports;
  }

  public get dashboardReports():IDashboardSectionMetadata[] {
    return this._dashboardReports;
  }

  public set dashboardGroupReports(dashboardGroupReports:IDashboardGroupMetadata[]){
    this._dashboardGroupReports = dashboardGroupReports;
  }

  public get dashboardGroupReports():IDashboardGroupMetadata[] {
    return this._dashboardGroupReports;
  }

  public getDashboardSectionMetadata(section:IDashboardSection):IDashboardSectionMetadata {
    let dashboardSectionMetadata:IDashboardSectionMetadata = _.find(this.dashboardReports, (report:IDashboardSectionMetadata) => report.key === section?.key);

    // get section metadata by looking over the groups
    if(!dashboardSectionMetadata){
      _.each(this._dashboardGroupReports, (groupReport:IDashboardGroupMetadata) => {
        _.each(groupReport.reports, (report:IDashboardSectionMetadata) => {
          if(report.key === section?.key){
            dashboardSectionMetadata = report;
            return;
          }
        });
      });
    }

    return dashboardSectionMetadata;
  }

  // replace the filter keys with the one from the dashboard. eg: {Decurce: 'EUR', Devincl: '0'} -> {currency: 'EUR', vatIncl: '0'}
  public filtersToDashboardFilters(filters:IObject):IObject {
    const fixedFilters:IObject = {};
    _.each(filters, (value:any, key:string) => {
      const dashboardKey:string = this.filterKeyToDashboardFilterKey(key);
      if(dashboardKey){
        fixedFilters[dashboardKey] = value;
      }
    });
    return fixedFilters;
  }

  public filterKeyToDashboardFilterKey(filterKey:string):string {
    const mapping:IObject = this.getExtraMapping();
    const dashboardValue:string = mapping[filterKey];
    const fieldKey:string[] = dashboardValue?.split('.');
    return fieldKey && fieldKey[0];
  }

  // set preselected option values based on the query filters
  public applyQueryFiltersToModel(filters:IObject, model:IObject, fields:IExtraFieldConfigurationObject[]):void {
    const mappingFieldModel:IObject = this.getMappingFieldModel();

    _.each(fields, (_field:IExtraFieldConfigurationObject) => {

      const fieldSelector = (field:IExtraFieldConfigurationObject):void => {
        switch(field.type){

          case `${FormlyType.MULTI_SELECT}`:
            field.formControl.setValue(util.csvToArray(filters[<string>field.key]) || model[ <string>field.key ] || undefined);
            break;

          case `${FormlyType.COMBINED_DATE}`:
            field.formControl.setValue(filters[<string>field.key] || model[ <string>field.key ] || undefined);
            break;

          case `${FormlyType.GROUP}`:
            _.each(field?.data?.fields, (f:IExtraFieldConfigurationObject) => {
              fieldSelector(f);
            });
            break;

          default: {
            const selectedOption:any[] = _.find(field.templateOptions.options, (opt:any) => {
              const mappingFieldKey:string = util.getValue(opt, mappingFieldModel[ <string>field.key ]) || opt.id;
              // eslint-disable-next-line eqeqeq
              return filters[ <string>field.key ] == mappingFieldKey; // use '==' to match both strings and numbers
            });
            field.formControl?.setValue(selectedOption || filters[<string>field.key] || model[ <string>field.key ] || undefined);
          }
        }
      };

      fieldSelector(_field);

    });
  }

  // in some KPIs (e.g. OD) the bp mapping is defined at the report level; in this case extra mapping is merged with mapping
  public getExtraMapping():IObject {
    const mapping:IObject = _.clone(this._dashboardMetadata?.mapping);
    const extraMapping:IObject = _.clone(this._dashboardMetadata?.extraMapping || {});
    return _.extend(mapping, extraMapping);
  }

  // creates an object based on the mappings. Eg. {Odlname: 'od.id', Pacomnm: 'bp.companyCountryId', Decurce: 'currency.id'} -> {od: 'id', bp: 'companyCountryId', currency: 'id'}
  private getMappingFieldModel():IObject {
    const mappingFieldModel:IObject = {};
    const separator:string = '.';
    const mapping:IObject = this.getExtraMapping();
    _.each(mapping, (dashboardKey:string) => {
      const dashboardKeySplit:string[] = dashboardKey.split(separator);
      const dashboardKeyFirst:string = dashboardKeySplit.shift();
      mappingFieldModel[dashboardKeyFirst] = dashboardKeySplit.join(separator);
    });
    return mappingFieldModel;
  }

  public createReportRefs(dashboardSections:IDashboardSection[]|IDashboardSection):void {
    const sections:IDashboardSection[] = _.isArray(dashboardSections) ? dashboardSections : [dashboardSections];
    sections.forEach((section:IDashboardSection) => {
      const defaultFilters:string[] = this.reportFiltersUtil.getDefaultFilters(section.report.gridMetadata?.columns);
      const reportId:number = section.report.browseListConfig? section.report.browseListConfig.reportId : section.report.id;

      section.report.ref = this.createDefaultRepRef(section, true);

      if (section.extraTextValue && section.extraTextValue.report) {
        section.extraTextValue.report.ref = this.createDefaultRepRef(section, false);
      }

      if (section.report.browseListCode) {
        section.report.ref.params[ReportParameterCode.BROWSELIST_CODE] = section.report.browseListCode;
      }

      // When is defined an alternate report use its configuration
      const dashboardSectionMetadata:IDashboardSectionMetadata = this.getDashboardSectionMetadata(section);
      if (dashboardSectionMetadata.alternateReport && dashboardSectionMetadata.alternateReport.id) {
        section.report.ref.params[ReportParameterCode.REPORT_ID] = dashboardSectionMetadata.alternateReport.id;
        section.report.ref.params[ReportParameterShortCode.FILTERS] = this.reportFiltersUtil.filtersToParam(this.makeSectionQueryFilters(dashboardSectionMetadata.alternateReport, this.dashboardMetadata), defaultFilters, { applyNullValues: true });
        section.report.ref.params[ReportParameterShortCode.STORED_REPORT] = dashboardSectionMetadata.alternateReport.storedReport;
        section.report.ref.params[ReportParameterShortCode.EXPORT_TEMPLATE] = dashboardSectionMetadata.alternateReport.exportTemplate;
      } else {
        section.report.ref.params[ReportParameterCode.REPORT_ID] = reportId;
        section.report.ref.params[ReportParameterShortCode.FILTERS] = this.reportFiltersUtil.filtersToParam(this.makeSectionQueryFilters(dashboardSectionMetadata, this.dashboardMetadata), defaultFilters);
        section.report.ref.params[ReportParameterShortCode.STORED_REPORT] = dashboardSectionMetadata.storedReport;
        section.report.ref.params[ReportParameterShortCode.EXPORT_TEMPLATE] = dashboardSectionMetadata.exportTemplate;
        const dashboardExtraTextValue = dashboardSectionMetadata.extraTextValue;
        if (dashboardExtraTextValue && dashboardExtraTextValue.report) {
          section.extraTextValue.report.ref.params[ReportParameterCode.REPORT_ID] = section.extraTextValue.report.id;
          section.extraTextValue.report.ref.params[ReportParameterShortCode.FILTERS] = this.reportFiltersUtil.filtersToParam(this.makeSectionQueryFilters(dashboardExtraTextValue.report, this.dashboardMetadata));
        }
      }
    });
  }

  private createDefaultRepRef(section?:IDashboardSection, includeTitle?:boolean):IDashboardSectionStateRef {
    const defaultReportRef:IDashboardSectionStateRef = {
      state: section.report.browseListCode? STATE_BROWSELIST : STATE_REPORT,
      params: {
        [ReportParameterShortCode.SHOW_FILTERS]: true,
        [ReportParameterShortCode.SHOW_COMMENTS]: true
      }
    };
    if (includeTitle) {
      defaultReportRef.params[ReportParameterShortCode.TITLE] = this.translationService.instant(section.name);
    }

    return defaultReportRef;
  }

  public makeSectionQueryFilters = (reportConfig:IDashboardReport, dashboardMetadata?:IDashboardMetadata, reportMetadata?:IReportMetadata, embeddedReportFilters?:IObject):IObject => {
    let sectionQueryFilters = {};

    // create a temporary scope to get the values of the filters from the model
    // this allows for complex expressions e.g. gbdm.firstname + " " + gbdm.lastname
    // let angular expression engine do the heavy lifting
    const tempScope:IObject = {};

    _.forEach(this.appliedFilters, (value:any, key:string) => {
      tempScope[ key ] = value;
    });

    // embedded report filters can have stored report filters
    // this should be the lowest precedences because it can be overwritten by selected filters in filters component and fixedFilters
    if (embeddedReportFilters) {
      // use pick and identity to remove 'undefined' object keys
      _.forEach(_.pick(embeddedReportFilters, _.identity), (filterValue:any, filterProp:string) => {
        sectionQueryFilters[filterProp] = filterValue;
      });
    }

    if (dashboardMetadata) {
      //map applied filters on dashboard level
      _.forEach(dashboardMetadata.mapping, (filterProp:string, keyProp:string) => {
        sectionQueryFilters[ keyProp ] = util.getValue(tempScope, filterProp);
      });
    }
    //map applied filters on section level
    if (reportConfig.mapping) {
      _.forEach(reportConfig.mapping, (filterProp:string, qfProp:string) => {
        sectionQueryFilters[ qfProp ] = util.getValue(tempScope, filterProp);
      });
    }

    //map fixed filters on section level
    if (reportConfig.fixedFilters) {
      sectionQueryFilters = _.extend(sectionQueryFilters, reportConfig.fixedFilters);
    }

    if (util.notNullOrUndefinedOrEmpty(reportMetadata)) {
      sectionQueryFilters = this.reportFiltersUtil.getQueryFiltersFromModel(sectionQueryFilters, reportMetadata, {
        applyDefaultValues: true,
        applyNullValues: true
      });
    }

    return sectionQueryFilters;
  };

  /**
   * Changes the columns of the grid metadata according to the sections column list.
   * These things can be updated: visibility, label and order
   *
   * @param section The section for which to do this
   * @param metadata The original metadata
   * @returns {IReportMetadata} The original metadata or a copy with the list of columns adjusted
   */
  public filterVisibleColumns(section:IDashboardSection, metadata:IReportMetadata):IReportMetadata {
    const configuredColumns:(string | IDashboardSectionColumn)[] = this.getDashboardSectionMetadata(section).columns;

    if (configuredColumns) { // we want to only show the columns that are needed in this dashboard
      const gridMetadata = util.deepClone(metadata);
      gridMetadata.columns = _.chain(gridMetadata.columns)
        .map((column) => {
          const configuredColumn:string|IDashboardSectionColumn = findConfiguredColumn(column);
          const shouldShow:boolean = !!configuredColumn;
          column.formatting.defaultinlist = shouldShow;
          column.formatting.showinlist = shouldShow;
          if (shouldShow) {
            column.label = (<IDashboardSectionColumn>configuredColumn).label || column.label;
            column.groupingSequence = (<IDashboardSectionColumn>configuredColumn).groupingSequence;
            column.aggregateOn = (<IDashboardSectionColumn>configuredColumn).aggregate;
          }
          column.listSequence = configuredColumns.indexOf(findConfiguredColumn(column));
          return column;
        })
        .value();
      return gridMetadata;
    } else {
      return metadata;
    }

    function findConfiguredColumn(column):string | IDashboardSectionColumn {
      return _.find(configuredColumns, (configuredColumn) => {
        return configuredColumn === column.name ||
          (<IDashboardSectionColumn>configuredColumn).name && (<IDashboardSectionColumn>configuredColumn).name === column.name;
      });
    }
  }

  public getInvalidValidations(validations:IDashboardSectionValidation[]):IDashboardSectionValidation[] {
    return _.filter(validations, (validation:IDashboardSectionValidation) => validation.invalid);
  }

  public toggleSection(section:IDashboardSection, collapsed:boolean, noScroll?:boolean):void {
    section.collapsed = collapsed;

    if (!section.collapsed && !noScroll) {
      this.scrollService.scrollToElement(`#${section.id}`, this.scrollOffset);
    }

    // because the dashboard section is collapsed by default, ag-grid is not calculating the correct width for grouped columns
    // this fixes it by resizing the grouped columns of the report when the section is expanded
    if(!collapsed) {
      setTimeout(() => this.reportGridService.autoSizeGroupedColumns(section.id));
    }
  }

  public loadReportsMetadata(dashboardSections:IDashboardSection[]):Promise<IDashboardMetadataReports> {
    const reportIdsToLoad:number[] = this.getReportsIdsToLoad(dashboardSections);

    // resolve when no new reports metadata to load (the case when report metadata is already loaded)
    if(reportIdsToLoad?.length === 0){
      return <any>Promise.resolve();
    }

    return this.dashboardDataService.loadReportsMetadata(reportIdsToLoad)
      .then((reportsMetadata:IDashboardMetadataReports) => {
        const reportIdsLoaded:number[] = [];
        _.each(reportsMetadata, (reportMetadata:IReportMetadata, reportId:string) => {
          if(reportId !== 'undefined') {
            this._loadedMetadataReports[ reportId ] = reportMetadata;
            reportIdsLoaded.push(Number(reportId));
          }
        });

        // if the requested report IDs are not found in the metadata response, remove the corresponding sections associated with those report IDs
        const reportIdsToRemove:number[] = _.difference(reportIdsToLoad, reportIdsLoaded);
        if(reportIdsToRemove.length){
          this.eventService.sendMessage(EVENT_DASHBOARD_REMOVE_REPORTS, reportIdsToRemove);
        }

        return this._loadedMetadataReports;
      });
  }

  public loadStoredReports(dashboardSections:IDashboardSection[]):Promise<IDashboardStoredReports> {
    // decide what stored reports to load
    const storedReportCodesToLoad:string[] = [];
    _.each(dashboardSections, (dashboardSection:IDashboardSection) => {
      if(dashboardSection.report.storedReport && !this._loadedStoredReportCodes.includes(dashboardSection.report.storedReport)) { // check if report stored report is already loaded
        storedReportCodesToLoad.push(dashboardSection.report.storedReport);
        this._loadedStoredReportCodes.push(dashboardSection.report.storedReport);
      }
    });

    // resolve when there are no new stored reports to load (covers the case when stored report is already loaded or no stored reports at all)
    if(storedReportCodesToLoad?.length === 0){
      return <any>Promise.resolve();
    }

    return this.dashboardDataService.loadStoredReports(storedReportCodesToLoad)
      .then((storedReports:IDashboardStoredReports) => {
        _.each(storedReports, (storedReport:IStoredReport) => {
          this._loadedStoredReports[storedReport.code] = storedReport;
        });
        return this._loadedStoredReports;
      });
  }

  public loadLayoutSettings(dashboardCode:string, ownerId:number, dashboardSections:IDashboardSection[]):Promise<void> {
    const gridCodes:string[] = _.map(dashboardSections, (section:IDashboardSection) => `${dashboardCode}_${section.key}`);
    return this.reportLayoutSettingsService.loadReportLayoutSettings(gridCodes)
      .then((layoutSettings:IReportLayoutSetting[])=> {
        _.each(dashboardSections, (section:IDashboardSection) => {
          section.report.layoutSetting = _.findWhere(layoutSettings, { gridCode: section.report.layoutGridCode, ownerId });
        });
      });
  }

  // decide what metadata reports to load
  private getReportsIdsToLoad(dashboardSections:IDashboardSection[]):number[] {
    const reportIdsToLoad:number[] = [];

    const addReportIdToList = (reportId:number) => {
      if(!this._loadedMetadataReportsIds.includes(reportId)) { // check if report metadata is already loaded
        reportIdsToLoad.push(reportId);
        this._loadedMetadataReportsIds.push(reportId);
      }
    };

    _.each(dashboardSections, (dashboardSection:IDashboardSection) => {
      // report
      if(dashboardSection?.report?.id){
        addReportIdToList(dashboardSection.report.id);
      }
      // browselist
      else if(dashboardSection?.report?.browseListCode){
        addReportIdToList(dashboardSection.report?.browseListConfig?.reportId);
      }
    });

    return reportIdsToLoad;
  }
}